import dayjs from "dayjs";
import { Calendar, CalendarStyles, Popover } from "frost-ui-react";
import React, { useEffect, useRef, useState } from "react";
import "react-datepicker/dist/react-datepicker.css";
import styled from "styled-components";
import { DATE_TIME_FORMAT_TYPES } from "../../../configs/constants/DateTimeFormatTypes";
import useOutsideAlerter from "../../../hooks/useOutsideAlerter";
import { appTheme } from "../../../theme/Theme";
import { getBrowserLocale } from "../../../utils/date.time.utils";
import AppInput, { AppInputProps } from "../AppInput/AppInput";
import AppText from "../AppText/AppText";

export type DateRange = {
	start: Date;
	end: Date;
};

interface AppDatePickerProps
	extends Pick<
		AppInputProps,
		"placeholder" | "label" | "margin" | "containerMargin" | "containerWidth" | "mandatory"
	> {
	onChange?: (range: DateRange | undefined) => any;
	value?: { start: Date; end: Date };
	/**
	 * Will if set make sure that popover open justified right.
	 * Otherwise defaults to left.
	 */
	justifyRight?: boolean;
	error?: string;
}

const AppRangePicker: React.FC<AppDatePickerProps> = ({
	onChange,
	value,
	justifyRight,
	error,
	...inputProps
}) => {
	const [hasFocus, setFocus] = useState(false);
	const [inputValue, setInputValue] = useState(dateRangeToString(value));
	const [rangeValue, setRangeValue] = useState(value);

	const wrapperRef = useRef(null);

	useOutsideAlerter(wrapperRef, () => {
		setFocus(false);

		// Revert back to orig value if no complete range is selected when closed
		if (!rangeValue) {
			setRangeValue(value);
		}
	});

	function handleChange(range?: DateRange) {
		if (range?.end) {
			// Make sure that range is inclusive end date
			range.end = dayjs(range.end).locale("sv").endOf("day").toDate();
		}
		setRangeValue(range);
		onChange && range && onChange(range);
	}

	useEffect(() => {
		setInputValue(dateRangeToString(rangeValue));
	}, [rangeValue]);

	return (
		<Wrapper ref={wrapperRef}>
			<AppInput
				icon="calendar"
				onFocus={() => setFocus(true)}
				value={inputValue}
				readOnly={true}
				{...inputProps}
			/>
			{hasFocus && (
				<CalendarWrapper top={inputProps.label ? "5rem" : "3rem"} justifyRight={justifyRight}>
					<Popover
						pointTo="top"
						styles={{ width: 400, offset: justifyRight ? `${400 - 60}px` : "25px" }}
					>
						<Calendar
							onChangeRange={handleChange}
							rangeValue={rangeValue}
							locale={getBrowserLocale()}
							styles={calendarStyle}
							focusOnDate={rangeValue?.start || new Date()}
							canSelectMonth
						/>
					</Popover>
				</CalendarWrapper>
			)}
			{error && (
				<AppText margin="0.75rem" size="s" color="danger">
					{error}
				</AppText>
			)}
		</Wrapper>
	);
};

export default AppRangePicker;

const Wrapper = styled.div`
	position: relative;
	min-width: 240px;
`;

const CalendarWrapper = styled.div<{ top: string; justifyRight?: boolean }>`
	position: absolute;
	z-index: 2;
	top: ${(props) => props.top};
	${(props) => (props.justifyRight ? `right: 0;` : "")}
`;

const calendarStyle: CalendarStyles = {
	rangeStyles: {
		value: {
			backgroundColor: appTheme.colors.COLOR_DARK_GRAY,
			color: "#222",
			handle: {
				backgroundColor: appTheme.colors.COLOR_ACTION_BLUE,
				color: "#fff",
			},
			preview: {
				color: "#222",
				backgroundColor: appTheme.colors.COLOR_DARK_GRAY,
			},
		},
	},
};

function dateRangeToString(range?: DateRange) {
	if (!range) {
		return "";
	}

	return (
		dayjs(range.start).format(DATE_TIME_FORMAT_TYPES.DATE) +
		"  -  " +
		dayjs(range.end).format(DATE_TIME_FORMAT_TYPES.DATE)
	);
}
